import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import globalResize from '../util/globalResize';

gsap.registerPlugin(ScrollTrigger);

export class HomeMessage {
  constructor() {
    this.DOM = {};
    this.DOM.section = document.querySelector('[data-home-message]');
    if (!this.DOM.section) return;

    this.DOM.texts = this.DOM.section.querySelectorAll('[data-home-message-element="text"]');
    this.DOM.photos = this.DOM.section.querySelectorAll('[data-home-message-element="photo"]');
    this.DOM.lines = document.querySelectorAll('[data-home-line]');
    this.DOM.maincopy = this.DOM.section.querySelector('[data-home-message-element="maincopy"]');
    this.DOM.annotations = this.DOM.section.querySelectorAll('[data-home-message-element="annotation"]');

    this.originalTextHTML = [...this.DOM.texts].map((el) => el.innerHTML);
    this.textTriggers = [];
    this.lastWidth = window.innerWidth;
    this.resizeTimer = null;

    this.init();
  }

  init() {
    this.setupPhotos();
    this.setupLines();
    this.setupMaincopy();
    this.setupAnnotations();

    const setupText = () => {
      this.splitTexts();
      this.animateTexts();
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(setupText);
    } else {
      setupText();
    }

    this.handlerResize = () => this.onResize();
    globalResize.addEvent(this.handlerResize);

    window.addEventListener('load', () => {
      ScrollTrigger.refresh();
    }, { once: true });
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (this.lastWidth === window.innerWidth) return;
      this.lastWidth = window.innerWidth;
      this.rebuildText();
    }, 150);
  }

  rebuildText() {
    this.textTriggers.forEach((trigger) => trigger.kill());
    this.textTriggers = [];
    this.DOM.texts.forEach((el, index) => {
      el.innerHTML = this.originalTextHTML[index];
      el.removeAttribute('data-home-message-ready');
    });
    this.splitTexts();
    this.animateTexts();
    ScrollTrigger.refresh();
  }

  splitTexts() {
    this.DOM.texts.forEach((el) => {
      this.wrapChars(el);
      this.groupLines(el);
      el.setAttribute('data-home-message-ready', '');
    });
  }

  wrapChars(el) {
    // supタグ内のテキストは分割せずタグごと残す
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.parentElement?.closest('sup') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    });
    const textNodes = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((node) => {
      const text = node.textContent;
      if (!text) return;

      const fragment = document.createDocumentFragment();
      for (const char of text) {
        if (char === '\n' || char === '\r' || char === '\t') continue;
        const span = document.createElement('span');
        span.setAttribute('data-home-message-char', '');
        span.textContent = char === ' ' ? '\u00A0' : char;
        fragment.appendChild(span);
      }
      node.parentNode.replaceChild(fragment, node);
    });

    // supは直前の1文字のspan内に入れ、文字と一緒に扱う
    el.querySelectorAll('sup').forEach((sup) => {
      const prevChar = sup.previousElementSibling;
      if (prevChar?.hasAttribute('data-home-message-char')) {
        prevChar.appendChild(sup);
      }
    });
  }

  groupLines(el) {
    const chars = [...el.querySelectorAll('[data-home-message-char]')];
    const groups = [];
    let lastTop = null;
    let current = [];

    chars.forEach((char) => {
      const top = Math.round(char.offsetTop);
      if (lastTop === null || Math.abs(top - lastTop) > 2) {
        if (current.length) groups.push(current);
        current = [char];
        lastTop = top;
      } else {
        current.push(char);
      }
    });
    if (current.length) groups.push(current);

    groups.forEach((group) => {
      const line = document.createElement('span');
      line.setAttribute('data-home-message-text-line', '');
      group[0].parentNode.insertBefore(line, group[0]);
      group.forEach((char) => line.appendChild(char));
    });

    el.querySelectorAll('br').forEach((br) => br.remove());
  }

  animateTexts() {
    this.DOM.section.querySelectorAll('[data-home-message-text-line]').forEach((line) => {
      const tween = gsap.fromTo(
        line,
        { opacity: 0.2 },
        {
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: line,
            start: 'top 78%',
            end: 'top 68%',
            scrub: true,
          },
        }
      );
      if (tween.scrollTrigger) this.textTriggers.push(tween.scrollTrigger);
    });
  }

  // 注釈は擬似要素（※）を含むため文字分割せず、要素全体をテキストと同じ動きでフェードさせる
  setupAnnotations() {
    this.DOM.annotations.forEach((annotation) => {
      gsap.fromTo(
        annotation,
        { opacity: 0.2 },
        {
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: annotation,
            start: 'top 78%',
            end: 'top 68%',
            scrub: true,
          },
        }
      );
    });
  }

  setupPhotos() {
    this.DOM.photos.forEach((photo) => {
      gsap.fromTo(
        photo,
        { y: 48, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: photo,
            start: 'top 85%',
            end: 'top 72%',
            scrub: 0.3,
          },
        }
      );
    });
  }

  setupLines() {
    this.DOM.lines.forEach((line) => {
      const target = line.querySelector('[data-home-line-image]') || line;
      gsap.fromTo(
        target,
        { x: -24, y: 24, opacity: 0 },
        {
          x: 0,
          y: 0,
          opacity: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: line,
            start: 'top 85%',
            end: 'top 72%',
            scrub: 0.3,
          },
        }
      );
    });
  }

  setupMaincopy() {
    if (!this.DOM.maincopy) return;
    const gif = this.DOM.maincopy.querySelector('[data-home-message-gif]');
    if (!gif) return;

    const src = gif.getAttribute('data-home-message-gif');
    if (!src) return;

    const setHref = (url) => {
      gif.setAttribute('href', url);
      gif.setAttributeNS('http://www.w3.org/1999/xlink', 'href', url);
    };

    let played = false;
    const play = () => {
      if (played) return;
      played = true;

      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        setHref(src);
        this.DOM.maincopy.setAttribute('data-home-message-play', '');
      };

      const preload = new Image();
      preload.addEventListener('load', start, { once: true });
      preload.src = src;
      if (preload.complete && preload.naturalWidth) start();
    };

    ScrollTrigger.create({
      trigger: this.DOM.maincopy,
      start: 'bottom bottom',
      once: true,
      onEnter: play,
    });
  }
}
